import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X, ShoppingBag, CalendarCheck } from "lucide-react";
import { useTray } from "../context/TrayContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/menu", label: "Menu" },
  { to: "/about", label: "Our Story" },
  { to: "/about#inquiry", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { count } = useTray();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname, location.hash]);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 border-b bg-nosh-green transition-shadow duration-500 ${
          scrolled ? "border-nosh-gold/30 shadow-[0_4px_24px_rgba(0,0,0,0.2)]" : "border-nosh-gold/20"
        }`}
      >
        <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-2 md:px-10">
          <Link to="/" data-testid="brand-logo" className="flex flex-col items-center leading-none">
            <span className="grid place-items-center rounded-full bg-nosh-cream p-1 shadow-md ring-1 ring-nosh-gold/40">
              <img src="/nosh-logo.png" alt="Nosh Pakistani Catering" className="h-[62px] w-[62px] object-contain sm:h-[86px] sm:w-[86px]" />
            </span>
            <span className="-mt-1.5 font-script text-lg font-bold text-nosh-gold sm:-mt-2 sm:text-xl">Nosh — farmaiye!</span>
          </Link>

          <div className="hidden items-center gap-1 lg:flex">
            {links.map((l) => (
              <NavLink
                key={l.label}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 font-poster text-[13px] font-semibold uppercase tracking-wide transition-colors ${
                    isActive && l.to !== "/about#inquiry"
                      ? "bg-nosh-cream/15 text-nosh-cream"
                      : "text-nosh-cream/75 hover:text-nosh-gold"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-2.5">
            <Link
              to="/about#inquiry"
              data-testid="nav-quote-cta"
              className="hidden items-center gap-2 rounded-full border-2 border-nosh-gold px-4 py-2 font-poster text-[12px] font-bold uppercase tracking-wide text-nosh-gold transition-colors hover:bg-nosh-gold hover:text-nosh-ink sm:flex"
            >
              <CalendarCheck className="h-4 w-4" />
              Get a Quote
            </Link>

            <Link
              to="/menu#builder"
              data-testid="nav-tray-button"
              className="relative grid h-10 w-10 place-items-center rounded-full bg-nosh-cream text-nosh-green transition-colors hover:bg-nosh-gold hover:text-nosh-ink"
              aria-label="Your order"
            >
              <ShoppingBag className="h-5 w-5" />
              {count > 0 && (
                <span
                  data-testid="nav-tray-count"
                  className="absolute -right-1.5 -top-1.5 grid h-5 min-w-5 place-items-center rounded-full bg-nosh-gold px-1 text-[11px] font-black text-nosh-ink ring-2 ring-nosh-green"
                >
                  {count}
                </span>
              )}
            </Link>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((o) => !o)}
              aria-label="Menu"
              className="grid h-10 w-10 place-items-center rounded-full text-nosh-cream lg:hidden"
            >
              {open ? <X className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="fixed inset-x-0 top-[116px] z-40 mx-4 rounded-2xl border border-nosh-border bg-nosh-cream p-5 shadow-2xl lg:hidden"
          >
            <div className="flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.label}
                  to={l.to}
                  end={l.to === "/"}
                  className="rounded-lg px-3 py-3 font-display text-2xl font-semibold text-nosh-ink hover:bg-nosh-surface"
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/about#inquiry"
                className="mt-3 rounded-full bg-nosh-green px-5 py-3 text-center font-poster text-sm font-bold uppercase tracking-wide text-nosh-cream"
              >
                Get a Quote
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
